import { apiGet, planningPath } from './PlanningApiClient';
import { convertCurrency } from '../utils/currency';

/** Raw per-allocation cost inputs for one bundle, across every order it was
 *  ever sourced from — see GET .../bundles/:id/allocations. Currency
 *  conversion and the landed-cost multiplier are computed client-side (see
 *  computeBundleActualCost) rather than duplicated server-side. */
export interface BundleAllocationCostInput {
  allocation_id: string;
  alloc_qty: number;
  /** Which procurement item this allocation is for — a bundle recipe calls
   *  for several distinct components (see computeBundleActualCost below), so
   *  this is what lets rows be grouped back into "one component" instead of
   *  averaged together as if they were interchangeable units of one thing. */
  item_id: string;
  product_name: string;
  unit_price_cents: number;
  item_currency: string;
  order_id: string;
  landing_cost_cents: number;
  contingency_pct: number;
  tax_pct: number;
  order_total_cents: number;
}

export async function loadBundleAllocations(bundleId: string): Promise<BundleAllocationCostInput[]> {
  const res = await apiGet<{ allocations: BundleAllocationCostInput[] }>(
    planningPath(`/bundles/${bundleId}/allocations`)
  );
  return res?.allocations ?? [];
}

/** One distinct procurement item's contribution to a bundle's recipe —
 *  its real landed unit cost (in the bundle's target currency) and how many
 *  units of it have actually been sourced/allocated to this bundle. */
export interface BundleComponentCost {
  itemId: string;
  productName: string;
  landedUnitCost: number;
  allocatedQty: number;
}

/**
 * Per-component landed unit costs for a bundle — one entry per distinct
 * procurement item allocated to it (allocations for the same item across
 * multiple orders are combined into one weighted-average unit cost and a
 * summed quantity, since they're still one recipe component just sourced
 * more than once). Landing cost/contingency/tax on each allocation's order
 * apply to the WHOLE order, not just this item, so they're spread
 * proportionally by that item's share of the order's total item value
 * (order_total_cents) — same logic ProcurementOrderEdit uses for its own
 * grand-total display, just applied per allocation instead of per order.
 */
export function computeBundleComponentCosts(
  rows: BundleAllocationCostInput[],
  targetCurrency: string
): BundleComponentCost[] {
  const byItem = new Map<string, { productName: string; costTotal: number; qty: number }>();
  for (const r of rows) {
    const landedMultiplier = r.order_total_cents > 0
      ? (r.order_total_cents + r.landing_cost_cents) * (1 + r.contingency_pct / 100 + r.tax_pct / 100) / r.order_total_cents
      : 1;
    const itemLandedUnitCostCents = r.unit_price_cents * landedMultiplier;
    const allocCost = convertCurrency((itemLandedUnitCostCents * r.alloc_qty) / 100, r.item_currency, targetCurrency);
    const existing = byItem.get(r.item_id);
    if (existing) {
      existing.costTotal += allocCost;
      existing.qty += r.alloc_qty;
    } else {
      byItem.set(r.item_id, { productName: r.product_name, costTotal: allocCost, qty: r.alloc_qty });
    }
  }
  return [...byItem.entries()].map(([itemId, v]) => ({
    itemId,
    productName: v.productName,
    landedUnitCost: v.qty > 0 ? v.costTotal / v.qty : 0,
    allocatedQty: v.qty,
  }));
}

/**
 * Actual per-SET landed cost across all allocations sourced for a bundle,
 * converted into `targetCurrency` — the number to compare against
 * `bundles.target_cost`. A bundle recipe is made of several DISTINCT
 * components (Reception Box = 1 Brandy XO + 1 Handcream + 1 Perfume + 1
 * Gift box), not interchangeable units of one thing — this used to sum
 * `alloc_qty` across every allocation regardless of which item it was and
 * divide total cost by that sum, which for Reception Box (720 Brandy + 720
 * Handcream) reported "1440 sourced" (really 720 complete sets) and
 * AVERAGED the two components' costs instead of ADDING them, roughly
 * halving the true per-set landed cost. The correct per-set cost is the SUM
 * of each component's own landed unit cost; the correct "sets sourced" is
 * bottlenecked by the scarcest component (the same bottleneck logic
 * AsoebiManager's bundleSellability already uses for makeable/sellable
 * counts), not their sum.
 */
export function computeBundleActualCost(
  rows: BundleAllocationCostInput[],
  targetCurrency: string
): { actualCostPerUnit: number; allocatedQuantity: number } | null {
  const components = computeBundleComponentCosts(rows, targetCurrency);
  if (components.length === 0) return null;
  const costPerSet = components.reduce((sum, c) => sum + c.landedUnitCost, 0);
  const setsSourced = Math.min(...components.map(c => c.allocatedQty));
  if (setsSourced <= 0) return null;
  return { actualCostPerUnit: costPerSet, allocatedQuantity: setsSourced };
}
